import { absolute, add, gte, money, sub, toDb, ZERO, type Money } from '@miraki/money';

/**
 * A-40 Sales & Payables desk and R-22 Payables — what both screens read, and the few rules the
 * screens apply to it. Pure, so it is tested without a browser.
 *
 * NOTHING here computes the payable, an invoice's open balance, an ageing band or overdue. Those
 * arrive from the API, which composes the shared functions (`outstandingPayable()` per currency,
 * A-42's `invoiceOpen()` and `PAYABLE_AGEING`). The arithmetic below is only what a person is
 * TYPING — the subtotal of the pieces they ticked, the sum of the allocations they entered — so
 * they can see it before they submit, and the server checks it again (FN-04 refuses a mismatch).
 * Money is decimal strings through `@miraki/money`, and only ever within one currency (AN-08).
 */

/*
 * The response shapes these two screens read now live in `@miraki/contracts` (`payables.ts`).
 * They were declared here because no route served them; the route does now, and the server
 * composes against the same types, so a field it fails to send is a compile error rather than a
 * blank cell. Re-exported so this module stays the one import for both screens.
 */
export type {
  AgeingBand, CurrencyAmount, Desk, DueRule, InvoiceRow, MyPayables, SoldPiece, StatementLine,
} from '@miraki/contracts';
import type { DueRule, InvoiceRow, SoldPiece } from '@miraki/contracts';

// ------------------------------------------------------------------ not yet

/*
 * "Not built yet" is ONE rule, `notYet()` in shop/consignment-read.ts (R-20/R-21): a 501
 * `{ code: 'NOT_YET', waitingFor }`, and nothing else. A-40's writes answer in that shape, so both
 * screens use it rather than carry a second reader.
 */
export { notYet } from './shop/consignment-read';

// ------------------------------------------------------------------ figures as words

/** A payable below zero is money Miraki owes the retailer (FN-08) — shown as a credit, never as 0. */
export const isCredit = (amount: string) => !gte(money(amount), ZERO);
export const magnitude = (amount: string) => toDb(absolute(money(amount)));

/**
 * **The one sentence that says when an invoice falls due. Every screen reads it from here.**
 *
 * The i18n key and values that describe the due rule **in force** — this account's own term
 * where it has one (D75), the portal's rule (SC-07) where it does not. `DueRule` carries both
 * shapes and the API has already resolved which applies, so nothing here decides anything; it
 * states what was decided.
 *
 * **Why no screen may build this itself.** "Net 30" is a SHAPE of answer that only fits
 * `NET_DAYS`. Four screens were printing it off `paymentTermsDays`, and for an account falling
 * back to a `MONTH_END` portal that is not merely the wrong number — it is the wrong kind of
 * sentence, describing a rule the portal does not operate. A screen cannot get that right from a
 * day count, which is why it is handed a rule and this is the only formatter.
 *
 * `form` picks which of the two readings the caller needs. A full SENTENCE is for a line of its
 * own; a PHRASE is for naming the rule inside someone else's sentence. Interpolating the sentence
 * into the record-invoice hint produced "Leave blank to use Payment is due at the end of the month
 * the invoice falls in, plus 7 days.." — two verbs, two full stops. A TERM is the shortest
 * reading, for a table cell with a column header already saying what it is.
 */
export function dueRuleWords(
  rule: DueRule, form: 'sentence' | 'phrase' | 'term' = 'sentence',
): { key: string; values: Record<string, number> } {
  const ns = form === 'phrase' ? 'payables.dueRuleShort'
    : form === 'term' ? 'payables.dueRuleTerm'
      : 'payables.dueRule';
  return rule.type === 'NET_DAYS'
    ? { key: `${ns}.netDays`, values: { days: rule.days } }
    : { key: `${ns}.monthEnd`, values: { days: rule.graceDays } };
}

/**
 * D75 — **where the rule in force came from**, as an i18n key. Reported, never inferred.
 *
 * Admin reading A-03 or A-04 must be able to tell "this account was given 60 days" from "nobody
 * decided, so the portal's rule applies" — the first is a commercial agreement with this
 * customer, the second is a default that changes the day the portal's setting does. A sentence
 * that states only the rule cannot distinguish them, and the distinction is the reason the
 * column is nullable at all.
 *
 * `voice` because the two readers are told different facts about the same rung. Admin is told
 * whose decision it was; the retailer is told whether it is theirs to discuss — "agreed for your
 * account" against "set by Miraki for your portal".
 */
export function dueRuleSourceWords(
  rule: DueRule, voice: 'admin' | 'retailer' = 'admin',
): { key: string } {
  return { key: `payables.dueRuleSource.${voice}.${rule.source}` };
}

/**
 * FN-09 — invoices where the market invoices per sale, payment history where it settles monthly.
 * Read from the portal's due rule: MONTH_END is FN-05's monthly payable, NET_DAYS FN-04's invoice
 * per sale. Inferred — the BRD ties the two by market (AUS, GCC), and the rule is the portal
 * setting that says which model a portal runs, without naming a country (D46).
 */
export const firstTab = (rule: DueRule): 'invoices' | 'payments' => (rule.type === 'MONTH_END' ? 'payments' : 'invoices');

// ------------------------------------------------------------------ recording an invoice (FN-04)

/**
 * An invoice covers ONE retailer's pieces in ONE currency: it is one retailer's debt
 * (`recordInvoice` takes one retailerId) and a document in one currency (AN-08). So a selection
 * that spans two is refused before the form opens, with the reason, rather than by the server.
 */
export function invoiceSelection(selected: readonly SoldPiece[]):
  | { ok: true; retailerId: string; retailerName: string; currency: string; subtotal: string; pieceIds: string[] }
  | { ok: false; reason: 'NONE' | 'RETAILERS' | 'CURRENCIES' } {
  if (selected.length === 0) return { ok: false, reason: 'NONE' };
  if (new Set(selected.map((p) => p.retailerId)).size > 1) return { ok: false, reason: 'RETAILERS' };
  if (new Set(selected.map((p) => p.currency)).size > 1) return { ok: false, reason: 'CURRENCIES' };
  const subtotal = selected.reduce<Money>((t, p) => add(t, money(p.wholesaleValue)), ZERO);
  return {
    ok: true, retailerId: selected[0]!.retailerId, retailerName: selected[0]!.retailerName,
    currency: selected[0]!.currency, subtotal: toDb(subtotal), pieceIds: selected.map((p) => p.pieceId),
  };
}

// ------------------------------------------------------------------ confirming a payment (FN-06, FN-08)

const AMOUNT = /^\d{1,15}(\.\d{1,4})?$/;

/**
 * What the person has typed into the payment form, checked as they type. The server is the
 * authority (zConfirmSalePayment); this says the same thing earlier.
 *
 *  - every allocation is a plain positive amount, at most what that invoice still owes;
 *  - the allocations do not exceed the payment;
 *  - what is left over is a CREDIT on the account (FN-08) — shown, not refused.
 */
export function allocationCheck(amount: string, allocations: ReadonlyArray<{ invoice: InvoiceRow; amount: string }>):
  | { ok: true; allocated: string; credit: string; lines: Array<{ invoiceId: string; amount: string }> }
  | { ok: false; problem: 'AMOUNT' | 'LINE' | 'OVER_INVOICE' | 'OVER_PAYMENT' | 'NOTHING_ALLOCATED'; reference?: string } {
  if (!AMOUNT.test(amount.trim()) || money(amount.trim()).isZero()) return { ok: false, problem: 'AMOUNT' };
  const lines: Array<{ invoiceId: string; amount: string }> = [];
  let allocated: Money = ZERO;
  for (const a of allocations) {
    const v = a.amount.trim();
    if (v === '') continue;
    if (!AMOUNT.test(v)) return { ok: false, problem: 'LINE', reference: a.invoice.reference };
    const m = money(v);
    if (m.isZero()) continue;
    if (!gte(money(a.invoice.open), m)) return { ok: false, problem: 'OVER_INVOICE', reference: a.invoice.reference };
    allocated = add(allocated, m);
    lines.push({ invoiceId: a.invoice.id, amount: toDb(m) });
  }
  if (lines.length === 0) return { ok: false, problem: 'NOTHING_ALLOCATED' };
  const paid = money(amount.trim());
  if (!gte(paid, allocated)) return { ok: false, problem: 'OVER_PAYMENT' };
  return { ok: true, allocated: toDb(allocated), credit: toDb(sub(paid, allocated)), lines };
}
